import { useStore } from '@nanostores/solid'
import { Show } from 'solid-js'
import SmallPlantIcon from '~/assets/images/ic-small-plant.svg'
import AppBlockIcon from '~/assets/images/ic_app_block_new.svg'
import NoAdsIcon from '~/assets/images/ic_no_ads.svg'
import RealTreeIcon from '~/assets/images/ic_real_tree.svg'
import SeasonalTreeIcon from '~/assets/images/ic_seasonal_tree_new.svg'
import StatsIcon from '~/assets/images/ic_stats.svg'
import ThumbsUpIcon from '~/assets/images/ic_trail_free.svg'
import TripleIcon from '~/assets/images/ic_triple.svg'
import { BottomLinks } from '~/components/bottom-links'
import { CloseButton } from '~/components/close-button'
import { ComparisonTable } from '~/components/comparison-table'
import { DevTools } from '~/components/dev-tools'
import { DiscountSection } from '~/components/discount-section'
import { FeatureItem } from '~/components/feature-item'
import { FooterDescription } from '~/components/footer-description'
import { GradientBorderBox } from '~/components/gradient-border-box'
import { Bg } from '~/components/graphics/bg'
import { Hero } from '~/components/hero'
import { PlusBadge } from '~/components/plus-badge'
import { SubscribeButton } from '~/components/subscribe-button'
import { Title } from '~/components/title'
import { m } from '~/paraglide/messages'
import {
  $chinaAndroidTrialEligibility,
  $isSubscribeButtonReady,
  $isTrialEligible,
} from '~/store'

const shouldShowDevTools = import.meta.env.MODE !== 'production'

export function App() {
  const isTrialEligible = useStore($isTrialEligible)
  const isSubscribeButtonReady = useStore($isSubscribeButtonReady)
  const chinaAndroidTrialEligibility = useStore($chinaAndroidTrialEligibility)

  return (
    <div class="relative mx-auto min-h-screen max-w-full bg-[#001721] pb-[120px] text-white">
      {/* Background Image */}
      <div class="absolute left-0 right-0 top-0 h-[900px] overflow-x-hidden bg-top">
        <div class="absolute left-1/2 top-0 h-full -translate-x-1/2">
          <Bg />
        </div>
      </div>

      <CloseButton />

      {/* Main Content */}
      <div class="z-5 relative items-center px-8 pb-12 pt-[calc(var(--safe-area-top)+13px)]">
        <div class="mx-auto flex w-full max-w-[640px] flex-col items-center gap-9">
          <div class="flex w-full flex-col items-center gap-6">
            {/* Header */}
            <div class="flex w-full flex-col items-center gap-3">
              <div class="flex items-center gap-2">
                <h2 class="text-base font-semibold text-white">Forest</h2>
                <PlusBadge />
              </div>
              <h1
                class="text-center text-[32px] font-bold text-white"
                style="text-shadow: 0px 0px 12px rgba(214, 248, 255, 0.53)"
              >
                <Show
                  when={
                    isTrialEligible() ||
                    chinaAndroidTrialEligibility() === 'available'
                  }
                  fallback={
                    <Title
                      message={m.onboarding_paywall_title}
                      interpolate={m.onboarding_paywall_color_title}
                      highlightKey="number_times_longer"
                    />
                  }
                >
                  <Title
                    message={m.new_user_paywall_title_free_ver}
                    interpolate={m.free_ver}
                    highlightKey="free"
                  />
                </Show>
              </h1>
            </div>

            {/* Hero Animation */}
            <Hero />

            {/* Premium Features List */}
            <GradientBorderBox
              borderWidth={3}
              radius={21}
              gradient="linear-gradient(to bottom, #1DFFFF1A, #1DFFFF00)"
              className="w-full bg-[#00A1E81A]"
            >
              <div class="flex flex-col gap-4 p-[21px]">
                <FeatureItem
                  icon={SeasonalTreeIcon}
                  text={m.onboarding_paywall_benefit_exclusive_content()}
                />
                <FeatureItem
                  icon={StatsIcon}
                  text={m.onboarding_paywall_benefit_focus_analysis()}
                />
                <FeatureItem
                  icon={TripleIcon}
                  text={m.onboarding_paywall_benefit_triple_coin()}
                />
                <FeatureItem
                  icon={RealTreeIcon}
                  text={m.onboarding_paywall_benefit_real_tree()}
                />
                <FeatureItem
                  icon={AppBlockIcon}
                  text={m.onboarding_paywall_benefit_custom_app_block_list()}
                />
                <FeatureItem
                  icon={NoAdsIcon}
                  text={m.onboarding_paywall_benefit_no_ads()}
                />
              </div>
            </GradientBorderBox>

            <ComparisonTable />
          </div>

          {/* Bottom Section */}
          <div class="flex w-full flex-col items-center gap-8">
            {/* Testimonial */}
            <div class="flex flex-col items-center gap-5 px-6">
              <div class="h-[120px] w-[136px] bg-cover">
                <img src={ThumbsUpIcon} width={136} height={120} alt="" />
              </div>
              <p
                class="text-center text-xl font-medium text-[#F9F9F9]"
                style="text-shadow: 0px 0px 12px rgba(214, 248, 255, 0.5)"
              >
                {m.onboarding_paywall_social_proof()}
              </p>
              <div class="flex h-5 w-full items-center gap-1 px-3">
                <div class="h-0.5 w-full rounded-full bg-[#FFFFFF33]" />
                <div class="size-5 shrink-0">
                  <img src={SmallPlantIcon} width={20} height={20} alt="" />
                </div>
                <div class="h-0.5 w-full rounded-full bg-[#FFFFFF33]" />
              </div>
            </div>

            <DiscountSection />

            <div class="flex justify-center gap-6 px-6">
              <BottomLinks />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        class="fixed bottom-0 left-0 right-0 z-30 bg-[#001721] px-6 pb-12 pt-3 transition-transform duration-700"
        classList={{ 'translate-y-full': !isSubscribeButtonReady() }}
        style="transition-timing-function: cubic-bezier(0.5,1,0.89,1);"
      >
        <div class="mx-auto flex w-full max-w-[640px] flex-col items-center">
          <SubscribeButton />
          <div class="h-3" />
          <FooterDescription />
        </div>
      </div>

      <Show when={shouldShowDevTools}>
        <DevTools />
      </Show>
    </div>
  )
}
